import { useEffect, useState } from "react";
import { CalendarCheck2, CalendarClock, Download, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ViewingActions from "@/components/viewings/viewing_actions";
import { VIEWING_UPDATED_EVENT, broadcastViewingUpdate } from "@/components/viewings/viewing_chat_card";
import { icsUrl, slotLabel, mockViewings } from "@/components/viewings/viewing_utils";

// Chat viewing strip. Whoever did NOT make the latest proposal (proposed_by)
// gets Approve / Decline / Counter propose; the proposer sees a waiting
// state. A counter flips proposed_by server side, so this panel swaps roles
// automatically on refresh. Once confirmed both roles get the banner with an
// "Add to calendar" (.ics) download.
export default function ViewingPanel({ matchId, mode, externalViewing }) {
  const { user } = useAuth();
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/viewings/mine")
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res.data) ? res.data : res.data?.viewings || res.data?.items || [];
        const mine = list.filter((v) => v.match_id === matchId && v.status !== "cancelled" && v.status !== "declined");
        setViewing(mine.find((v) => v.status === "confirmed") || mine[mine.length - 1] || null);
      })
      .catch(() => {
        // Offline — browse the UI states with a mock proposal.
        if (!alive) return;
        const mine = mockViewings(matchId).filter((v) => v.match_id === matchId);
        setViewing(mine[0] || null);
      });
    return () => {
      alive = false;
    };
  }, [matchId]);

  useEffect(() => {
    if (externalViewing) setViewing(externalViewing);
  }, [externalViewing]);

  // Decisions can also happen on the in chat viewing cards below; keep this
  // strip in sync so it never offers buttons on an already decided viewing.
  useEffect(() => {
    const onUpdated = (e) => {
      const next = e.detail;
      if (next?.match_id === matchId) {
        setViewing((v) => (v && v.id === next.id ? { ...v, ...next } : v));
      }
    };
    window.addEventListener(VIEWING_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(VIEWING_UPDATED_EVENT, onUpdated);
  }, [matchId]);

  if (!viewing) return null;

  function cancel() {
    api.post(`/viewings/${viewing.id}/cancel`).catch(() => {});
    broadcastViewingUpdate({ ...viewing, status: "cancelled" });
    setViewing(null);
    toast.success("Viewing cancelled.");
  }

  function onDecision(next) {
    setViewing(next);
    broadcastViewingUpdate(next);
  }

  if (viewing.status === "confirmed" && viewing.accepted_slot) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-night-line bg-night-tint px-4 py-2.5">
        <p className="flex min-w-0 flex-1 items-center gap-2 text-sm font-semibold text-night-text">
          <CalendarCheck2 size={15} className="shrink-0 text-brand-primary" />
          <span className="truncate">Viewing confirmed · {slotLabel(viewing.accepted_slot)}</span>
        </p>
        <div className="flex items-center gap-1.5">
          <a
            href={icsUrl(viewing.id)}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-primary-hover"
          >
            <Download size={12} /> Add to calendar
          </a>
          <button
            type="button"
            onClick={cancel}
            aria-label="Cancel viewing"
            className="rounded-full p-1.5 text-night-sub transition hover:bg-night-surface hover:text-night-text"
          >
            <X size={13} />
          </button>
        </div>
      </div>
    );
  }

  if (viewing.status !== "proposed") return null;

  // Who owes the reply: the side that did NOT make the latest proposal.
  // Legacy docs without proposed_by were always lister proposals, and mock
  // demo sessions have no real user id, so mode is the fallback either way.
  const myId = user?.id || user?._id;
  const iProposed = viewing.proposed_by
    ? viewing.proposed_by === myId
    : mode === "lister";

  if (iProposed) {
    return (
      <div className="border-b border-night-line bg-night-surface px-4 py-3">
        <p className="flex items-center gap-2 text-sm font-semibold text-night-text">
          <CalendarClock size={15} className="text-brand-primary" />
          Viewing time sent. Waiting for the other side to approve, decline, or counter propose.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {(viewing.slots || []).map((slot) => (
            <span key={slot} className="rounded-full bg-night-tint px-3 py-1.5 text-xs font-medium text-night-sub">
              {slotLabel(slot)}
            </span>
          ))}
          <button
            type="button"
            onClick={cancel}
            className="text-xs font-semibold text-night-sub underline-offset-2 transition hover:text-night-text hover:underline"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-night-line bg-night-surface px-4 py-3">
      <p className="flex items-center gap-2 text-sm font-semibold text-night-text">
        <CalendarClock size={15} className="text-brand-primary" />
        {(viewing.slots || []).length > 1
          ? "A viewing was proposed. Tap a time to approve it, or reply another way."
          : "A viewing time was proposed. Approve it, decline, or counter propose."}
      </p>
      <div className="mt-2">
        <ViewingActions viewing={viewing} onUpdate={onDecision} />
      </div>
    </div>
  );
}
